import React from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { 
  CalendarToday, 
  WarningAmber, 
  Schedule, 
  StarBorder, 
  BugReportOutlined 
} from '@mui/icons-material';
import { BACKLOG_CARD_TYPES } from '../../../shared/appConstants';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../../shared/colors';
import { Skeleton } from '@mui/material';

const BacklogMetricCards = React.memo(({
  isLoading,
  isExpandedView,
  selectedCardType,
  setSelectedCardType,
  activeSprint,
  setCurrentMainView,
  formatDateRange,
  activeSprintP1Pending,
  activeSprintDaysRemaining,
  activeSprintProgress,
  upcomingPlannedSprint,
  upcomingPlannedSprintCount,
  upcomingPlannedSprintDates,
  activeSprintTiming,
  activeSprintTicketCount,
  myHighPriorityCount,
  myBugsCount,
  myTicketsCount
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className={`backlog-metrics-grid ${isExpandedView ? 'collapsed' : ''}`}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="backlog-metric-card" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', minHeight: '130px', border: `1.5px solid ${COLORS.slate[200]}` }}>
            <Skeleton variant="text" width="50%" height={28} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="40%" height={20} />
              </div>
              <Skeleton variant="circular" width={44} height={44} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`backlog-metrics-grid ${isExpandedView ? 'collapsed' : ''}`}>
      {/* Metric 1: Active Sprint */}
      <div 
        className={`backlog-metric-card purple-theme ${selectedCardType === BACKLOG_CARD_TYPES.ACTIVE_SPRINT ? 'active-card' : ''}`}
        onClick={() => {
          setSelectedCardType(BACKLOG_CARD_TYPES.ACTIVE_SPRINT);
          setCurrentMainView(activeSprint?.id);
        }}
      >
        <div className="metric-header">{t('backlog.activeSprint')}</div>
        <div className="metric-body">
          <div className="metric-left">
            <span className="metric-label">{t('backlog.impedimentsFound')}</span>
            <div className="metric-row">
              <CalendarToday className="icon" />
              <span>{activeSprintTiming}</span>
            </div>
          </div>
          <div className="metric-right">
            <span className="metric-value">{activeSprintTicketCount}</span>
          </div>
        </div>
      </div>

      {/* Metric 2: Sprint Completion Rate */}
      <div 
        className={`backlog-metric-card blue-theme ${selectedCardType === 'completion-rate' ? 'active-card' : ''} ${!activeSprint ? 'disabled-nav' : 'clickable'}`}
        onClick={() => {
          if (activeSprint) {
            navigate('/sprint-management');
          }
        }}
      >
        <div className="metric-header">{t('backlog.sprintCompletionRate')}</div>
        <div className="metric-body">
          <div className="metric-left">
            <div className="metric-row red-text">
              <WarningAmber className="icon" />
              <span>{activeSprintP1Pending} {t('backlog.p1StoriesPending')}</span>
            </div>
            
            <div className="metric-row blue-text">
              <Schedule className="icon" />
              <span>{activeSprintDaysRemaining} {t('backlog.daysRemaining')}</span>
            </div>
          </div>
          <div className="metric-right">
            <div className="progress-ring-container-large">
              <svg width="100%" height="100%" viewBox="0 0 60 60" style={{ transform: 'rotate(90deg) scaleX(-1)' }}>
                <circle cx="30" cy="30" r="26" fill="transparent" stroke="#DBDBDB" strokeWidth="4" />
                <circle cx="30" cy="30" r="26" fill="transparent" stroke="#082B72" strokeWidth="4" 
                  strokeDasharray="163.3" strokeDashoffset={163.3 - (1.633 * activeSprintProgress)} strokeLinecap="round" />
              </svg>
              <div className="progress-ring-content">
                <span className="progress-ring-percentage">{activeSprintProgress}%</span>
                <span className="progress-ring-label">{t('backlog.complete')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metric 3: Planned Sprint */}
      <div 
        className={`backlog-metric-card green-theme ${selectedCardType === BACKLOG_CARD_TYPES.PLANNED_SPRINT ? 'active-card' : ''}`}
        onClick={() => {
          setSelectedCardType(BACKLOG_CARD_TYPES.PLANNED_SPRINT);
          setCurrentMainView(upcomingPlannedSprint?.id || 'sprint-2');
        }}
      >
        <div className="metric-header">{t('backlog.plannedSprint')}</div>
        <div className="metric-body">
          <div className="metric-left">
            <div className="metric-row">
              <CalendarToday className="icon" />
              <span>{t('backlog.upcomingPlannedSprint')}</span>
            </div>
            <span className="metric-sublabel">
              {upcomingPlannedSprintDates}
            </span>
          </div>
          <div className="metric-right">
            <span className="metric-value">{upcomingPlannedSprintCount}</span>
          </div>
        </div>
      </div>

      {/* Metric 4: My Tickets */}
      <div 
        className={`backlog-metric-card orange-theme ${selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS ? 'active-card' : ''}`}
        onClick={() => {
          setSelectedCardType(BACKLOG_CARD_TYPES.MY_TICKETS);
          setCurrentMainView('my_tickets');
        }}
      >
        <div className="metric-header">{t('backlog.myTickets')}</div>
        <div className="metric-body">
          <div className="metric-left">
            <div className="metric-row">
              <StarBorder className="icon" />
              <span>{myHighPriorityCount} {t('backlog.highPriority')}</span>
            </div>
            <div className="metric-row">
              <BugReportOutlined className="icon" />
              <span>{myBugsCount} {t('backlog.bug')}</span>
            </div>
          </div>
          <div className="metric-right">
            <span className="metric-value">{myTicketsCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
});


BacklogMetricCards.propTypes = {
  isLoading: PropTypes.bool,
  isExpandedView: PropTypes.bool.isRequired,
  selectedCardType: PropTypes.oneOf([
    BACKLOG_CARD_TYPES.ACTIVE_SPRINT,
    BACKLOG_CARD_TYPES.PLANNED_SPRINT,
    BACKLOG_CARD_TYPES.MY_TICKETS,
    'completion-rate'
  ]).isRequired,
  setSelectedCardType: PropTypes.func.isRequired,
  activeSprint: PropTypes.shape({
    id: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  setCurrentMainView: PropTypes.func.isRequired,
  formatDateRange: PropTypes.func.isRequired,
  activeSprintP1Pending: PropTypes.number.isRequired,
  activeSprintDaysRemaining: PropTypes.number.isRequired,
  activeSprintProgress: PropTypes.number.isRequired,
  upcomingPlannedSprint: PropTypes.shape({
    id: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  upcomingPlannedSprintCount: PropTypes.number.isRequired,
  upcomingPlannedSprintDates: PropTypes.string.isRequired,
  activeSprintTiming: PropTypes.string.isRequired,
  activeSprintTicketCount: PropTypes.number.isRequired,
  myHighPriorityCount: PropTypes.number.isRequired,
  myBugsCount: PropTypes.number.isRequired,
  myTicketsCount: PropTypes.number.isRequired
};

export default BacklogMetricCards;
